import RatingHelper from 'src/components/rating/rating-helper';
import type { Option } from 'src/components/filter-dropdown';
import { OptionTypeEnum } from 'src/components/filter-dropdown';
import type { TranslateFunc } from 'src/components/translate';

import type { Price, Store } from 'src/types';
import type { CurrentCampaign } from 'src/areas/product/types';
import type { Rating } from 'src/components/rating/types';
import type { Offer } from '../price-row/types';
import type { ExtendedPrice } from '../../product-context';

const getStoreRating = (store?: Store | null): Rating | undefined => {
  return store?.userReviewSummary?.rating
    ? RatingHelper.convert(store.userReviewSummary.rating)
    : undefined;
};

export const getCampaignByOfferId = (
  offerId?: string | null,
  campaign?: CurrentCampaign | null
) => {
  return campaign?.price &&
    campaign?.media &&
    campaign?.name &&
    offerId === campaign.price.id
    ? campaign
    : null;
};

export function createOfferFromPriceItem(item: ExtendedPrice): Offer {
  return {
    name: item.name,
    price: item.price,
    offerPrices: item.offerPrices,
    priceId: item.id,
    availability: item.availability,
    store: item.store,
    featured: Boolean(item.store?.featured),
    storeInfo:
      item.store && item.store.providedByStore
        ? item.store.providedByStore.generalInformation
        : null,
    rating: getStoreRating(item.store),
    stock: item.stock,
    externalUri: item.externalUri,
    alternativePrices: item.alternativePrices,
    authorizedDealer: Boolean(item.authorizedDealer),
    isInternationalPrice: Boolean(
      item.store && item.store.market === 'secondary'
    ),
    shipping: item.shipping,
    unfilteredShippings: item.unfilteredShippings,
    ourChoiceScore: item.ourChoiceScore,
  };
}

export const getSortOptions = (
  t: TranslateFunc,
  allOffers: Price[]
): Option[] => {
  const hasDeliveryDays = allOffers.some(offer =>
    offer.shipping?.nodes.some(node => node?.deliveryDays)
  );

  if (!hasDeliveryDays) {
    return [];
  }

  return [
    {
      title: t('campaigns', 'sort.price.offer|asc'),
      type: OptionTypeEnum.CHEAPEST_PRICE,
    },
    {
      title: t('common', 'sort.fastest-delivery'),
      type: OptionTypeEnum.FASTEST_DELIVERY,
    },
  ];
};

const shouldShowUnfeaturedPlaceholder = (list: Price[]) => {
  // Show placeholder if there's any featured offers and more than 1 unfeatured offer.
  const hasFeaturedOffers = Boolean(
    list.filter(item => Boolean(item.store?.featured && item.externalUri))
      .length
  );
  const hasSomeUnfeaturedOffers =
    list.filter(item => Boolean(!item.store?.featured || !item.externalUri))
      .length > 1;

  return hasFeaturedOffers && hasSomeUnfeaturedOffers;
};

export const getUnfeaturedPlaceholderData = (offers: Price[]) => {
  const firstUnfeaturedOfferIndex = offers.findIndex(item =>
    Boolean(!item.store?.featured || !item.externalUri)
  );

  const count = offers.filter(
    offer => (offer.store && !offer.store.featured) || !offer.externalUri
  ).length;

  return shouldShowUnfeaturedPlaceholder(offers)
    ? {
        count,
        id: 'unfeatured-placeholder-card',
        index: firstUnfeaturedOfferIndex,
        price: offers[firstUnfeaturedOfferIndex].price?.exclShipping,
      }
    : undefined;
};
